import {
	Api,
	GovernanceServiceClientState,
	GovernanceServiceHostPlatform,
	GovernanceServiceSurveyPlatform,
	GovernanceServiceSurveyType,
	GovernedChannelType,
	IGovernanceCampaignInfo,
	IGovernanceCampaignRequest,
	IGovernanceClientInfo,
	IGovernanceParameters,
	IGovernanceServiceResult
} from "@ms-ofb/floodgate-shared-typings";
import { isDate, isNOU, isNumber, isValidUserId, stringToDate } from "@ms-ofb/officefloodgatecore/dist/src/Utils";
import { Configuration } from "../Configuration/Configuration";
import { DynamicContentTransport } from "../DynamicContent/DynamicContentTransport";
import * as Logging from "../Logging/Logging";
import * as Constants from "./../Constants";

const govServiceIntURL = "https://onessppe.microsoft.com/api/v1.0/UserCampaigns";
const govServiceProdURL = "https://oness.microsoft.com/api/v1.0/UserCampaigns";

export class GovernanceServiceClient implements Api.IGovernanceServiceClient {

	// making these data public so they can be access for testing
	public dynamicTransport: DynamicContentTransport;
	public clientState: GovernanceServiceClientState;

	// Configuration data
	private governanceServiceEnabled: boolean;
	private governanceServiceEnabledChannels: GovernedChannelType[];
	private forceServicelessSurveyDisplay: boolean;
	private retryLimit: number;
	private timeout: number;
	private isProduction: boolean;
	private isSurveyGovernanceEnabled: boolean;

	// client info
	private clientInfo: IGovernanceClientInfo;
	private userId: string;

	// survey data
	private lastSurveyFailedSync: IGovernanceCampaignInfo;
	constructor() {
		this.governanceServiceEnabledChannels = [];
		this.forceServicelessSurveyDisplay = false;
		this.lastSurveyFailedSync = undefined;
		this.clientState = GovernanceServiceClientState.Disabled;
	}

	public setup(configuration: Configuration) {
		this.governanceServiceEnabled = configuration.getFloodgateInitOptions().governanceServiceEnabled;

		if (this.governanceServiceEnabled) {
			// setup config and client info
			this.clientInfo = {
				applicationId: configuration.getCommonInitOptions().appId.toString(),
				sessionId: configuration.getCommonInitOptions().sessionId,
				version: configuration.getCommonInitOptions().build,
				surveyPlatform: GovernanceServiceSurveyPlatform.Floodgate,
				hostPlatform: GovernanceServiceHostPlatform.Web,
				tenantId: configuration.getCommonInitOptions().telemetryGroup.tenantId,
				deviceId: configuration.getCommonInitOptions().telemetryGroup.deviceId,
				audience: configuration.getCommonInitOptions().telemetryGroup.audience,
			};
			this.userId = configuration.getCommonInitOptions().telemetryGroup.loggableUserId;

			// Ensure all the client info is needed
			if (isNOU(this.clientInfo.sessionId)) {
				this.clientState = GovernanceServiceClientState.InitializationFailed;
				return;
			}

			if (isNOU(this.clientInfo.version)) {
				this.clientState = GovernanceServiceClientState.InitializationFailed;
				return;
			}

			if (!isValidUserId(this.userId)) {
				this.clientState = GovernanceServiceClientState.InitializationFailed;
				return;
			}

			// Setup governance service config parameters
			this.forceServicelessSurveyDisplay = configuration.getFloodgateInitOptions().governanceServiceConfig.forceServicelessSurveyDisplay;
			this.retryLimit = configuration.getFloodgateInitOptions().governanceServiceConfig.retry;
			this.timeout = configuration.getFloodgateInitOptions().governanceServiceConfig.timeout;
			this.isProduction = configuration.getCommonInitOptions().environment === Constants.Environment.Production;
			this.clientState = GovernanceServiceClientState.Enabled;
			this.dynamicTransport = new DynamicContentTransport();
			this.isSurveyGovernanceEnabled = configuration.getFloodgateInitOptions().governanceServiceConfig.isSurveyLevelGovernanceEnabled;

			// Get feature gate settings
			const featureGateProvider = configuration.getFeatureGateProvider();
			if (!featureGateProvider) {
				return;
			}

			// Enable governance for standard and teaching by default, this feature gate will be a master swtich to disable/enable channels 
			this.governanceServiceEnabledChannels = featureGateProvider.getJsonObjectStringFeatureStatus(
				Constants.FeatureGateNames.GovernanceServiceEnabledChannels, 
				[GovernedChannelType.Standard, GovernedChannelType.TeachingMessage, GovernedChannelType.Dialog]);

			// Override userId for local testing
			const userIdOverride = featureGateProvider.getStringFeatureStatus(
				Constants.FeatureGateNames.GovernanceLocalUserIdOverride, "");
			if (userIdOverride) {
				this.userId = userIdOverride;
			}
		}
	}

	public isEnabled(governedChannelType: GovernedChannelType): boolean {
		return this.clientState === GovernanceServiceClientState.Enabled &&
			this.governanceServiceEnabled && 
			((this.governanceServiceEnabledChannels ?? []).indexOf(governedChannelType) !== -1);
	}

	public isSurveyLevelGovernanceEnabled(): boolean {
		return this.isSurveyGovernanceEnabled;
	}

	public isGovernanceEnabledForChannel(governedChannelType: GovernedChannelType): boolean {
		return this.isSurveyGovernanceEnabled || governedChannelType !== GovernedChannelType.Standard;
	}

	public shouldForceDisplay(): boolean {
		return this.forceServicelessSurveyDisplay;
	}

	public getClientInfo(): IGovernanceClientInfo {
		return this.clientInfo;
	}

	public getClientState(): GovernanceServiceClientState {
		return this.clientState;
	}

	public getLastSurveyFailedSync(): IGovernanceCampaignInfo {
		return this.lastSurveyFailedSync;
	}

	public async checkGovernancePermitAsync(governanceParams: IGovernanceParameters): Promise<IGovernanceServiceResult> {

		// If the GovernanceService is not enabled, return as allowance
		if (!this.isEnabled(governanceParams.channelType)) {
			return { permission: true, error: false };
		}

		const campaignInfo: IGovernanceCampaignInfo = this.buildCampaignInfo(governanceParams)
		const requestUrl = this.isProduction ? govServiceProdURL : govServiceIntURL;
		const body = this.buildRequestBody(campaignInfo, governanceParams.userCooldown);

		const serviceResult: IGovernanceServiceResult = {
			permission: false,
			error: false,
			isSurveyGoverned: false,
			requestId: this.dynamicTransport.requestId,
		};

		let response: Response;
		let serviceCallTime: number;

		try {
			const timeBefore = performance.now();
			response = await this.dynamicTransport.postRequestWithRetry(requestUrl, body, this.retryLimit, this.timeout);
			serviceCallTime = performance.now() - timeBefore;

			let responseText: string | undefined; /* eslint-disable-line prefer-const */

			if (!response.ok) {
				this.lastSurveyFailedSync = campaignInfo;
				serviceResult.errorMessage = `Server Error, status: ${response.status}, status text: ${response.statusText}`;
				serviceResult.error = true;
				// Log the successful post criteria with time
				Logging.getLogger().logEvent(Logging.EventIds.GOVERNANCE_SERVICERESULT,
					Logging.LogLevel.Critical,
					{
						TimeMilliseconds: serviceCallTime,
						HttpStatusCode: response.status,
						HttpStatusText: response.statusText,
						AdditionalSurveyInfo: this.buildConfigTelemetryString(),
						CorrelationId: this.dynamicTransport.requestId,
						GovernanceRequestBody: body,
						GovernanceResponseText: responseText,
						CampaignId: governanceParams.campaignId,
						SurveyId: governanceParams.surveyId,
						ChannelType: governanceParams.channelType,
					});

				return serviceResult;
			}

			// Get the response text
			responseText = await response.text();

			// Log the successful post criteria with time
			Logging.getLogger().logEvent(Logging.EventIds.GOVERNANCE_SERVICERESULT,
				Logging.LogLevel.Critical,
				{
					TimeMilliseconds: serviceCallTime,
					HttpStatusCode: response.status,
					HttpStatusText: response.statusText,
					AdditionalSurveyInfo: this.buildConfigTelemetryString(),
					CorrelationId: this.dynamicTransport.requestId,
					GovernanceRequestBody: body,
					GovernanceResponseText: responseText,
					CampaignId: governanceParams.campaignId,
					SurveyId: governanceParams.surveyId,
					ChannelType: governanceParams.channelType,
				});

			// Parse the response
			const jsonResponse = JSON.parse(responseText);

			// Reparsing the date to correct ISO format
			jsonResponse.lastTriggerCampaignTime = stringToDate(new Date(jsonResponse.lastTriggerCampaignTime).toISOString());

			// Check the response integrity
			if (
				!isNumber(jsonResponse.timeUntilNextCampaignAllowed) ||
				!isDate(jsonResponse.lastTriggerCampaignTime)
			) {
				// Throw error with the response
				serviceResult.errorMessage = "Service Response is not formatted correctly: " + responseText;
				serviceResult.error = true;
				this.lastSurveyFailedSync = campaignInfo;
			} else {
				const isCampaignGovernanceEnabled = this.isGovernanceEnabledForChannel(governanceParams.channelType);

				// Survey level Governance for this survey is enabled on server side.
				// Any null value (below) from service indicates that this specific survery is not governed.
				// We should bypass this check and continue the workflow.
				if (
					isCampaignGovernanceEnabled &&
					isNumber(jsonResponse.timeUntilSameCampaignAllowed) &&
					!isNOU(jsonResponse.lastTriggerSameCampaignTime)
				) {
					serviceResult.isSurveyGoverned = true;
					try {
						jsonResponse.lastTriggerSameCampaignTime = stringToDate(new Date(jsonResponse.lastTriggerSameCampaignTime).toISOString());
					} catch (e) {
						serviceResult.isSurveyGoverned = false;
					}
				}

				// Check if this is permitted
				if (
					jsonResponse.timeUntilNextCampaignAllowed === 0 &&
					((isCampaignGovernanceEnabled && serviceResult.isSurveyGoverned) ? jsonResponse.timeUntilSameCampaignAllowed === 0 : true)
				) {
					serviceResult.permission = true;
				}

				// Save the response trigger time
				serviceResult.lastTriggerSurvey = jsonResponse;

				// Clear the last failure
				this.lastSurveyFailedSync = undefined;
			}
		} catch (e) {
			this.lastSurveyFailedSync = campaignInfo;
			Logging.getLogger().logEvent(Logging.EventIds.GOVERNANCE_SERVICEFAILURE,
				Logging.LogLevel.Error,
				{
					ErrorMessage: e.toString(),
					AdditionalSurveyInfo: this.buildConfigTelemetryString(),
					CorrelationId: this.dynamicTransport.requestId,
					HttpStatusCode: response?.status,
					HttpStatusText: response?.statusText,
					GovernanceRequestBody: body,
				});
			serviceResult.error = true;
			serviceResult.errorMessage = `Error: ${e.toString()}, Error stack: ${e.stack}`;
		}

		return serviceResult;
	}
	
	private buildCampaignInfo(governanceParams: IGovernanceParameters): IGovernanceCampaignInfo {
		const stringType = Api.SurveyType[governanceParams.surveyType];
		const surveyType: number = GovernanceServiceSurveyType[stringType as keyof typeof GovernanceServiceSurveyType];
		const newDate = new Date();
		return {
			campaignId: governanceParams.campaignId,
			maxLaunches: governanceParams.maxLaunches,
			triggerTime: newDate,
			type: surveyType,
			cooldown: governanceParams.campaignCooldown,
			channel: governanceParams.channelType,

			localOffsetTime: newDate.getTimezoneOffset(),
			personalizerEnabled: governanceParams.personalizerEnabled,
			// Survey Level Governance Enabled/Disabled
			surveyGovernanceEnabled: this.isGovernanceEnabledForChannel(governanceParams.channelType),
		};
	}

	private buildRequestBody(campaignInfo: IGovernanceCampaignInfo, userLevelCooldown: number): string {
		const result: IGovernanceCampaignRequest = {
			userId: this.userId,
			campaign: campaignInfo,
			userLevelCooldown: userLevelCooldown,
			client: this.clientInfo,
			requestId: this.dynamicTransport.requestId,
		};

		return JSON.stringify(result);
	}

	private buildConfigTelemetryString() {
		const objectTelemetry = {
			retry: this.retryLimit,
			timeout: this.timeout,
		};
		return JSON.stringify(objectTelemetry);
	}
}

const governanceServiceClient: GovernanceServiceClient = new GovernanceServiceClient();

/**
 * Get the current Governance Service Client
 */
export function get(): GovernanceServiceClient {
	return governanceServiceClient;
}
